/**
 * Cookie utilities for anonymous user identification
 */

const USER_COOKIE_NAME = 'launchcraft_user_id'
const COOKIE_EXPIRY_DAYS = 365

/**
 * Generate a unique anonymous user ID
 * @returns {string} UUID-like identifier
 */
function generateUserId() {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * Set a cookie with the given name, value, and expiry days
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiry
 */
function setCookie(name, value, days) {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  
  // Set cookie with SameSite=Lax for security
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
function getCookie(name) {
  const nameEQ = `${name}=`
  const cookies = document.cookie.split(';')
  
  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length))
    }
  }
  
  return null
}

/**
 * Set the anonymous user cookie
 * Creates a new user ID if one doesn't exist
 * @returns {string} The user ID (new or existing)
 */
export function setUserCookie() {
  let userId = getCookie(USER_COOKIE_NAME)
  
  if (!userId) {
    userId = generateUserId()
    setCookie(USER_COOKIE_NAME, userId, COOKIE_EXPIRY_DAYS)
  }
  
  return userId
}

/**
 * Get the current user ID from cookies
 * @returns {string|null} The user ID or null if not set
 */
export function getUserId() {
  return getCookie(USER_COOKIE_NAME)
}

/**
 * Clear the user cookie (for testing/debugging)
 */
export function clearUserCookie() {
  document.cookie = `${USER_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
}
